import { AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { useEffect } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { DeliveryNote } from "@/types";

interface EvidenceAnalysisResult {
  summary?: string | null;
  condition_score?: number | null;
  alert?: boolean | null;
  alert_reason?: string | null;
  recommendation?: string | null;
}

interface EvidenceAnalysisProps {
  evidenceFiles: DeliveryNote["evidence_files"];
  analysisStatus?: string;
  analysisResults?: {
    filename: string;
    url: string;
    analysis?: EvidenceAnalysisResult | null;
    error?: string;
  }[];
  onRefresh?: () => void;
}

function conditionBadgeClasses(score: number) {
  if (score >= 8) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (score >= 5) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-red-100 text-red-700";
}

function alertBadgeClasses(alert?: boolean | null) {
  if (alert) {
    return "bg-red-100 text-red-700";
  }
  return "bg-emerald-100 text-emerald-700";
}

export default function EvidenceAnalysisSection({
  evidenceFiles,
  analysisStatus = "not_started",
  analysisResults = [],
  onRefresh,
}: EvidenceAnalysisProps) {
  // Auto-refresh every 5 seconds while analyzing
  useEffect(() => {
    if (analysisStatus === "analyzing") {
      const interval = setInterval(() => {
        onRefresh?.();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [analysisStatus, onRefresh]);

  if (evidenceFiles.length === 0) {
    return null;
  }

  const statusConfig: Record<
    string,
    { icon: typeof AlertCircle; label: string; color: string }
  > = {
    not_started: {
      icon: AlertCircle,
      label: "Pendiente",
      color: "text-muted-foreground",
    },
    analyzing: {
      icon: Clock,
      label: "Analizando...",
      color: "text-blue-600",
    },
    completed: {
      icon: CheckCircle2,
      label: "Completado",
      color: "text-green-600",
    },
    failed: { icon: XCircle, label: "Error", color: "text-red-600" },
  };

  const currentStatus = statusConfig[analysisStatus] || statusConfig.not_started;
  const StatusIcon = currentStatus.icon;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium">Análisis de Evidencias (IA)</h3>
          <div className={`flex items-center gap-1 ${currentStatus.color}`}>
            <StatusIcon className="h-4 w-4" />
            <span className="text-xs font-medium">{currentStatus.label}</span>
          </div>
        </div>
      </div>

      {analysisStatus === "analyzing" && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertTitle>Analizando evidencias</AlertTitle>
          <AlertDescription>
            Se está procesando el análisis de las imágenes. Esta página se actualizará automáticamente.
          </AlertDescription>
        </Alert>
      )}

      {analysisStatus === "failed" && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Error en el análisis</AlertTitle>
          <AlertDescription>
            Hubo un error al procesar las evidencias. Por favor, intenta más tarde.
          </AlertDescription>
        </Alert>
      )}

      {/* Evidence Items with Analysis */}
      <div className="grid gap-4">
        {evidenceFiles.map((file) => {
          const analysisEntry = analysisResults?.find(
            (result) => result.filename === file.filename
          );
          const analysis = analysisEntry?.analysis ?? null;
          const hasAlert = analysis?.alert === true;
          const score =
            typeof analysis?.condition_score === "number"
              ? analysis.condition_score
              : null;

          return (
            <div
              key={file.filename}
              className="space-y-2 rounded-lg border border-border p-4"
            >
              <div className="flex items-start gap-3">
                {/* Image thumbnail */}
                <div className="flex-shrink-0">
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="h-16 w-16 rounded object-cover"
                  />
                </div>

                {/* Analysis result */}
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {file.filename}
                  </p>

                  {analysisEntry ? (
                    <div className="space-y-3">
                      {analysisEntry.error ? (
                        <div className="rounded bg-red-50 p-2 text-xs text-red-700">
                          <p className="font-medium">Error</p>
                          <p>{analysisEntry.error}</p>
                        </div>
                      ) : analysis ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
                            {score !== null && (
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 ${conditionBadgeClasses(
                                  score
                                )}`}
                              >
                                Condición: {score}/10
                              </span>
                            )}
                            {analysis.alert !== null && analysis.alert !== undefined && (
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 ${alertBadgeClasses(
                                  analysis.alert
                                )}`}
                              >
                                {analysis.alert ? "Alerta detectada" : "Sin alertas"}
                              </span>
                            )}
                          </div>

                          {analysis.summary && (
                            <p className="text-sm leading-relaxed text-foreground">
                              {analysis.summary}
                            </p>
                          )}

                          {hasAlert && (
                            <Alert variant="destructive">
                              <AlertCircle className="h-4 w-4" />
                              <AlertTitle>Requiere atención</AlertTitle>
                              <AlertDescription>
                                {analysis.alert_reason ??
                                  "Se detectaron problemas en la evidencia."}
                              </AlertDescription>
                            </Alert>
                          )}

                          {analysis.recommendation && (
                            <div className="rounded border border-border/60 bg-muted p-2 text-xs text-muted-foreground">
                              <p className="font-semibold uppercase tracking-wide text-foreground">
                                Recomendación
                              </p>
                              <p className="mt-1 leading-relaxed">
                                {analysis.recommendation}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-xs italic text-muted-foreground">
                          Análisis no disponible para esta evidencia.
                        </p>
                      )}
                    </div>
                  ) : analysisStatus === "analyzing" || analysisStatus === "not_started" ? (
                    <p className="text-xs text-muted-foreground italic">
                      Pendiente de análisis…
                    </p>
                  ) : null}
                </div>

                {/* Download link */}
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-xs underline text-muted-foreground hover:text-foreground"
                >
                  Ver
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
